import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { FormikErrors, FormikTouched, useFormik } from 'formik';

import {
  Component,
  Header,
  StyledAlfaLeasingLogo,
  StyledButton,
  Title,
} from '@/components/modals/AlfaLeasingFormModal/AlfaLeasingFormModal.styles';
import { colors } from '@/constants/theme';
import Tabs from '@/components/Tabs';
import { media } from '@/utils/mixin';
import Loader from '@/components/Loader';
import Select from '@/components/Select';
import { OptionType } from '@/typings/common';
import CloseButton from '@/components/modals/shared/CloseButton';
import StaticTextInput from '@/components/StaticTextInput';

import { AlfaLeasingQuizFieldsType, AlfaLeasingQuizProps } from './types';
import {
  initialAlfaLeasingQuizValues,
  questions,
  selectData,
  tabsData,
  validationAverageIncome,
  validationBirthday,
  validationDebts,
  validationLastJob,
  validationNationality,
} from './AlfaLeasingQuiz.helpers';

const getFormatDate = (date: string) => {
  const splitDate = date.split('.');

  return `${splitDate[2]}-${splitDate[1]}-${splitDate[0]}`;
};

const getActiveComponent = (
  activeStep: number,
  activeTab?: string,
  onChangeTab?: (value: string) => void,
  selectValue?: OptionType,
  onChangeSelect?: (newSelectedValue: OptionType) => void,
  values?: AlfaLeasingQuizFieldsType,
  errors?: FormikErrors<AlfaLeasingQuizFieldsType>,
  handleChange?: (e: React.ChangeEvent<any>) => void,
  touched?: FormikTouched<AlfaLeasingQuizFieldsType>
) => {
  switch (activeStep) {
    case 1:
      return (
        <FieldRow>
          <StaticTextInput
            type="tel"
            id="birthdate"
            name="birthdate"
            mask={Date}
            min={new Date(1910, 0, 1)}
            max={new Date()}
            value={values?.birthdate}
            placeholder="дд / мм / гггг"
            onChange={handleChange}
            isWhiteBackground
            autoFocus
            errorMessage={
              errors?.birthdate && touched?.birthdate
                ? errors.birthdate
                : undefined
            }
          />
        </FieldRow>
      );

    case 2:
      return (
        <FieldRow>
          <StaticTextInput
            type="tel"
            id="lastJobAt"
            name="lastJobAt"
            mask={Date}
            min={new Date(1910, 0, 1)}
            max={new Date()}
            value={values?.lastJobAt}
            placeholder="дд / мм / гггг"
            onChange={handleChange}
            isWhiteBackground
            autoFocus
            errorMessage={
              errors?.lastJobAt && touched?.lastJobAt
                ? errors.lastJobAt
                : undefined
            }
          />
        </FieldRow>
      );

    case 3:
      return (
        <Select
          value={selectValue ?? selectData[0]}
          options={selectData}
          onChange={onChangeSelect}
        />
      );

    case 4:
      return (
        <FieldRow>
          <StaticTextInput
            type="tel"
            id="averageIncome"
            name="averageIncome"
            value={String(values?.averageIncome)}
            placeholder="1000"
            mask={Number}
            min={0}
            max={1000000}
            onChange={handleChange}
            isWhiteBackground
            autoFocus
            errorMessage={
              errors?.averageIncome && touched?.averageIncome
                ? errors.averageIncome
                : undefined
            }
          />
        </FieldRow>
      );

    case 5:
      return (
        <Tabs
          data={tabsData}
          activeTab={activeTab ?? ''}
          onChange={onChangeTab}
          isWhiteBackground
        />
      );
  }
};

const getValidationSchema = (activeStep: number) => {
  switch (activeStep) {
    case 1:
      return validationBirthday;
    case 2:
      return validationLastJob;
    case 3:
      return validationNationality;
    case 4:
      return validationAverageIncome;
    case 5:
      return validationDebts;
  }
};

function AlfaLeasingQuiz({
  onClose,
  onSubmitAlfaLeasingQuiz,
}: AlfaLeasingQuizProps) {
  const [activeStep, setActiveStep] = useState(1);
  const [activeTab, setActiveTab] = useState(tabsData[0].value);
  const [activeSelect, setActiveSelect] = useState(selectData[0]);

  const {
    values,
    errors,
    setValues,
    touched,
    handleChange,
    handleSubmit,
    isSubmitting,
  } = useFormik<AlfaLeasingQuizFieldsType>({
    initialValues: initialAlfaLeasingQuizValues,
    validationSchema: getValidationSchema(activeStep),
    onSubmit: async (values, { setSubmitting, setTouched }) => {
      if (activeStep === questions.length) {
        setSubmitting(true);

        try {
          const birthdate = getFormatDate(values.birthdate);
          const lastJobAt = getFormatDate(values.lastJobAt);

          const submitValues = {
            ...values,
            birthdate,
            lastJobAt,
            averageIncome: Number(values.averageIncome),
          };

          await onSubmitAlfaLeasingQuiz?.(submitValues);
        } catch (error) {
          console.error(error);
        }
      } else {
        setActiveStep(activeStep + 1);
      }

      setSubmitting(false);
      setTouched({});
    },
  });

  useEffect(() => {
    setValues({
      ...values,
      haveDebts: activeTab !== 'Нет',
      isBelarus: activeSelect.value === selectData[0].value,
    });
  }, [activeTab, activeSelect]);

  const handleChangeCitizen = (newSelectedValue: OptionType) => {
    setActiveSelect(newSelectedValue);
  };

  return (
    <Component onSubmit={handleSubmit} noValidate autoComplete="off">
      <CloseButton onClick={onClose} />

      <Header>
        <StyledAlfaLeasingLogo />
        <Title>Онлайн предодобрение</Title>
      </Header>

      <Content>
        <Steps>
          <ActiveStep>{activeStep}</ActiveStep>
          <StepsCount>/{questions.length}</StepsCount>
        </Steps>

        <Question>{questions[activeStep - 1]}</Question>

        <ElementWrapper>
          {getActiveComponent(
            activeStep,
            activeTab,
            setActiveTab,
            activeSelect,
            handleChangeCitizen,
            values,
            errors,
            handleChange,
            touched
          )}
        </ElementWrapper>

        <ButtonWrapper>
          <StyledButton
            variant="contained"
            type="submit"
            disabled={isSubmitting}
          >
            {!isSubmitting ? (
              activeStep !== questions.length ? (
                'Далее'
              ) : (
                'Результат'
              )
            ) : (
              <Loader color={colors.white} />
            )}
          </StyledButton>
        </ButtonWrapper>
      </Content>
    </Component>
  );
}

export default AlfaLeasingQuiz;

const Content = styled.div`
  margin-top: 33px;

  ${media.mobile(css`
    margin-top: 61px;
  `)}
`;

const Steps = styled.div`
  display: flex;
  align-items: flex-end;

  ${media.mobile(css`
    justify-content: center;
  `)}
`;

const ActiveStep = styled.span`
  font-weight: 700;
  font-size: 20px;
  line-height: 23px;
  color: ${colors.main};
`;

const StepsCount = styled.span`
  font-weight: 300;
  font-size: 16px;
  line-height: 21px;
  color: ${(props) => props.theme.black100_alpha50};
`;

const Question = styled.p`
  font-weight: 500;
  font-size: 18px;
  line-height: 21px;
  margin-top: 5px;
  color: ${(props) => props.theme.black100};

  ${media.mobile(css`
    text-align: center;
    margin-top: 55px;
  `)}
`;

const ElementWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  max-width: 286px;
  margin: 40px auto 0;

  ${media.mobile(css`
    max-width: 400px;
    margin-top: 56px;
  `)}
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 69px;

  ${media.mobile(css`
    margin-top: 150px;
  `)}
`;

const FieldRow = styled.div`
  position: relative;
  width: 100%;
`;
